'use client'

/**
 * Main cart hook backed by React Query.
 *
 * Replaces the old Zustand cart store. Provides cart data, loading state,
 * and computed values (itemCount, totals). Cart data is fetched via
 * server actions and kept fresh via React Query's stale-while-revalidate
 * strategy plus SSE invalidation (see useCartStream).
 *
 * Cart ID comes from server-rendered initial data or from mutation responses.
 * It is stored in React Query cache and persisted via cookies on the server side.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { HttpTypes } from '@medusajs/types'
import { cartKeys } from '@lib/query-keys'
import { cartQueryOptions } from '@lib/queries/cart'
import { useCallback, useRef, useEffect } from 'react'

// Module-level cart ID store for client-side tracking
// This is needed because cookies are server-only in Next.js
let _clientCartId: string | null = null
function rememberClientCartId(id: string) {
  _clientCartId = id
}

export function getClientCartId(): string | null {
  return _clientCartId
}

export function setClientCartId(cartId: string | null) {
  _clientCartId = cartId
}

/**
 * Hook to access and manage cart state via React Query.
 *
 * @param initialCart - Optional server-side fetched cart for hydration
 */
export function useCart(initialCart?: HttpTypes.StoreCart | null) {
  const queryClient = useQueryClient()
  const hasHydrated = useRef(false)

  // Hydrate cart ID from initial server data (once) — via a module helper: the react-compiler
  // lint forbids assigning a module variable inside the component body itself.
  if (initialCart?.id && !hasHydrated.current) {
    rememberClientCartId(initialCart.id)
    hasHydrated.current = true
  }

  const cartId = _clientCartId

  const { data: cart, isLoading, isFetching } = useQuery({
    ...cartQueryOptions(cartId),
    // Use server data as initial data for instant rendering
    ...(initialCart && !hasHydrated.current
      ? {}
      : initialCart
        ? { initialData: initialCart }
        : {}),
  })

  // Update client cart ID when cart data changes. Also promote the cart
  // into the id-scoped query key: on a fresh load / second tab the query
  // runs under the cartKeys.all fallback key (client id still null), so
  // without this the detail key stays empty and mutations keyed by id
  // would not see the hydrated cart.
  useEffect(() => {
    if (cart?.id && cart.id !== _clientCartId) {
      _clientCartId = cart.id
      queryClient.setQueryData(cartKeys.detail(cart.id), cart)
    }
  }, [cart?.id, cart, queryClient])

  // Computed: filter out service items (return shipping) for count
  const productItems = (cart?.items ?? []).filter(
    (item: HttpTypes.StoreCartLineItem) => !(item.metadata as any)?.isReturnShipping
  )

  const itemCount = productItems.reduce(
    (acc: number, item: HttpTypes.StoreCartLineItem) => acc + item.quantity,
    0
  )

  // Update the cart in React Query cache (used by mutations)
  const setCart = useCallback(
    (newCart: HttpTypes.StoreCart | null) => {
      if (newCart?.id) {
        _clientCartId = newCart.id
        queryClient.setQueryData(cartKeys.detail(newCart.id), newCart)
      } else if (cartId) {
        queryClient.setQueryData(cartKeys.detail(cartId), null)
      }
    },
    [cartId, queryClient]
  )

  // Invalidate cart queries to trigger refetch. Invalidate the fallback
  // key too: before the client id is known the live query runs under
  // cartKeys.all, so invalidating only the detail key would miss it.
  const invalidateCart = useCallback(() => {
    if (cartId) {
      queryClient.invalidateQueries({ queryKey: cartKeys.detail(cartId) })
    }
    queryClient.invalidateQueries({ queryKey: cartKeys.all })
  }, [cartId, queryClient])

  return {
    cart: cart ?? null,
    cartId,
    isLoading,
    isFetching,
    itemCount,
    subtotal: cart?.subtotal ?? 0,
    total: cart?.total ?? 0,
    currencyCode: cart?.currency_code ?? 'USD',
    items: cart?.items ?? [],
    productItems,
    setCart,
    invalidateCart,
  }
}
